/**
 * src/utils/offlineSyncQueue.js
 * Utilidad para gestionar la cola de sincronización offline de Caseta.
 */
import { collection, doc, addDoc, updateDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { registerMovement } from './registerMovement';

const QUEUE_KEY = 'offline_sync_queue';

/**
 * Obtiene la cola actual de localStorage.
 */
export const getSyncQueue = () => {
    try {
        const stored = localStorage.getItem(QUEUE_KEY);
        return stored ? JSON.parse(stored) : [];
    } catch (error) {
        console.error('Error parseando offline_sync_queue:', error);
        return [];
    }
};

/**
 * Agrega una operación a la cola pendiente.
 * @param {string} actionName - 'registerPaquete', 'markGuestExit', 'registerManualAccess', etc.
 * @param {Object} payload - Los datos necesarios para ejecutar la acción online.
 */
export const pushToSyncQueue = (actionName, payload) => {
    const queue = getSyncQueue();
    const item = {
        id: Date.now().toString() + '-' + Math.random().toString(36).substr(2, 9),
        actionName,
        payload,
        timestamp: Date.now(),
    };
    queue.push(item);
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
    console.log(`[OfflineSync] Encolado: ${actionName}`, item);
    return item.id;
};

/**
 * Remove un item específico de la cola.
 */
export const removeFromSyncQueue = (id) => {
    let queue = getSyncQueue();
    queue = queue.filter(item => item.id !== id);
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
};

/**
 * Vacía la cola completa.
 */
export const clearSyncQueue = () => {
    localStorage.removeItem(QUEUE_KEY);
};

/**
 * Procesa todos los items en la cola intentando ejecutarlos en Firebase.
 */
export const processSyncQueue = async (currentUser) => {
    if (!navigator.onLine || !currentUser || currentUser.isOffline) {
        console.log('[OfflineSync] Abortando sincronización: sin internet o usuario no verificado online.');
        return { success: false, message: 'Se requiere internet y sesión online para sincronizar.' };
    }

    const queue = getSyncQueue();
    if (queue.length === 0) {
        return { success: true, processed: 0 };
    }

    console.log(`[OfflineSync] Iniciando sincronización de ${queue.length} items...`);
    const functions = getFunctions();
    let processedCount = 0;
    const failedItems = [];
    let emailActionsProcessed = 0;

    for (const item of queue) {
        try {
            console.log(`[OfflineSync] Sincronizando item:`, item);
            const { actionName, payload } = item;
            const isEmailAction = ['sendReciboCobro', 'sendReceiptEmail', 'sendCustomBulletin'].includes(actionName);

            if (actionName === 'registerPaquete') {
                const registroRef = collection(db, 'fraccionamientos', payload.fraccionamientoId, 'paqueteria');
                await addDoc(registroRef, {
                    fraccionamientoId: payload.fraccionamientoId,
                    departamento: payload.departamento,
                    empresa: payload.empresa,
                    destinatario: payload.destinatario,
                    guardia: payload.guardia,
                    fechaRecepcion: Timestamp.fromDate(new Date(payload.fechaRecepcionIso)),
                    registradoEn: serverTimestamp(),
                    registradoPor: currentUser.uid,
                    destinatarioUid: payload.destinatarioUid,
                    estado: 'pendiente',
                });

                // Registrar movimiento asociado
                await registerMovement({
                    fraccionamientoId: payload.fraccionamientoId,
                    titulo: `Paquete recibido para ${payload.departamento}`,
                    descripcion: `(Sincronizado) Se registró paquetería de ${payload.empresa} para ${payload.destinatario}. Guardia: ${payload.guardia}.`,
                    categoria: 'Vigilancia',
                    tipoMovimiento: 'paqueteria_registrada',
                    origen: 'vigilancia',
                    metadata: {
                        departamento: payload.departamento,
                        empresa: payload.empresa,
                        destinatario: payload.destinatario,
                        guardia: payload.guardia,
                        destinatarioUid: payload.destinatarioUid,
                        sincronizadoOffline: true
                    },
                });

            } else if (actionName === 'markPackageDelivered') {
                let evidenciaUrl = '';
                if (payload.imageBase64) {
                    try {
                        const response = await fetch(payload.imageBase64);
                        const blob = await response.blob();
                        const fileName = `paqueteria/${payload.fraccionamientoId}/${payload.paqueteId}_entrega_${Date.now()}.jpg`;
                        const fileRef = storageRef(storage, fileName);
                        const snapshot = await uploadBytes(fileRef, blob);
                        evidenciaUrl = await getDownloadURL(snapshot.ref);
                    } catch (e) {
                        console.error('[OfflineSyncQueue] Fallo al subir imagen Base64 convertida:', e);
                    }
                }

                const packageRef = doc(db, 'fraccionamientos', payload.fraccionamientoId, 'paqueteria', payload.paqueteId);
                await updateDoc(packageRef, {
                    status: 'entregado',
                    estado: 'entregado',
                    fechaEntrega: serverTimestamp(),
                    entregadoEn: serverTimestamp(),
                    evidenciaEntrega: evidenciaUrl,
                    entregadoPor: payload.entregadoPor
                });

                await registerMovement({
                    fraccionamientoId: payload.fraccionamientoId,
                    titulo: 'Entrega de Paquete',
                    descripcion: `(Sincronizado) Paquete de ${payload.empresa || 'Empresa'} entregado a Depto ${payload.departamento}.`,
                    categoria: 'Vigilancia',
                    tipoMovimiento: 'paqueteria_entregada',
                    origen: 'vigilancia',
                    metadata: {
                        departamento: payload.departamento,
                        empresa: payload.empresa,
                        entregadoPorUid: payload.entregadoPor.uid,
                        sincronizadoOffline: true
                    },
                });

            } else if (actionName === 'markGuestExit') {
                const markExitFn = httpsCallable(functions, 'markGuestVisitExit');
                await markExitFn({
                    fraccionamientoId: payload.fraccionamientoId,
                    accessId: payload.accessId,
                    finalStatus: 'fuera',
                });

            } else if (actionName === 'validateQr') {
                const { codigo, imagenesUrls, fraccionamientoId } = payload;
                let syncedImagenesUrls = { ...imagenesUrls };

                // Procesar imágenes Base64 si existen
                for (const key in syncedImagenesUrls) {
                    const url = syncedImagenesUrls[key];
                    if (url && url.startsWith('data:image')) {
                        try {
                            const response = await fetch(url);
                            const blob = await response.blob();
                            const fileName = `visitantes/${fraccionamientoId}/qr/${codigo}_${key}_${Date.now()}.jpg`;
                            const fileRef = storageRef(storage, fileName);
                            const snapshot = await uploadBytes(fileRef, blob);
                            syncedImagenesUrls[key] = await getDownloadURL(snapshot.ref);
                        } catch (e) {
                            console.error(`[OfflineSync] Error subiendo imagen ${key} para QR:`, e);
                        }
                    }
                }

                const validateQrFn = httpsCallable(functions, 'validateGuestVisitQr');
                await validateQrFn({
                    fraccionamientoId,
                    codigo,
                    imagenesUrls: syncedImagenesUrls,
                });

            } else if (actionName === 'registerManualAccess') {
                const { fraccionamientoId, tipoAcceso, visitante, imagenesUrls } = payload;
                let syncedImagenesUrls = { ...(imagenesUrls || {}) };

                // Procesar imágenes Base64 si existen
                for (const key in syncedImagenesUrls) {
                    const url = syncedImagenesUrls[key];
                    if (url && url.startsWith('data:image')) {
                        try {
                            const response = await fetch(url);
                            const blob = await response.blob();
                            const fileName = `visitantes/${fraccionamientoId}/manual/${visitante.replace(/\s+/g, '_')}_${key}_${Date.now()}.jpg`;
                            const fileRef = storageRef(storage, fileName);
                            const snapshot = await uploadBytes(fileRef, blob);
                            syncedImagenesUrls[key] = await getDownloadURL(snapshot.ref);
                        } catch (e) {
                            console.error(`[OfflineSync] Error subiendo imagen ${key} para manual:`, e);
                        }
                    }
                }

                const registerAccessFn = httpsCallable(functions, 'registerManualGuestAccess');
                await registerAccessFn({
                    ...payload,
                    imagenesUrls: syncedImagenesUrls,
                });
            } else if (actionName === 'sendReciboCobro') {
                const sendReciboCobroFn = httpsCallable(functions, 'sendReciboCobro');
                await sendReciboCobroFn(payload);
            } else if (actionName === 'sendReceiptEmail') {
                const sendReceiptEmailFn = httpsCallable(functions, 'sendReceiptEmail');
                await sendReceiptEmailFn(payload);
            } else if (actionName === 'createReciboCobroQueue') {
                const createQueueFn = httpsCallable(functions, 'createReciboCobroQueue');
                const queueResponse = await createQueueFn(payload);
                if (queueResponse.data && queueResponse.data.success) {
                    const processQueueFn = httpsCallable(functions, 'processReciboCobroQueue');
                    await processQueueFn({
                        fraccionamientoId: payload.fraccionamientoId,
                        queueId: queueResponse.data.queueId,
                        batchSize: 60
                    });
                }
            } else if (actionName === 'sendCustomBulletin') {
                const storagePaths = [];
                if (payload.base64Files && payload.base64Files.length > 0) {
                    for (const fileObj of payload.base64Files) {
                        try {
                            const response = await fetch(fileObj.base64);
                            const blob = await response.blob();
                            const path = `boletines/${payload.fraccionamientoId || 'general'}/${Date.now()}-${fileObj.name}`;
                            const r = storageRef(storage, path);
                            const resRef = await uploadBytes(r, blob, { contentType: fileObj.type });
                            storagePaths.push({ name: fileObj.name, path: resRef.metadata.fullPath, contentType: fileObj.type });
                        } catch(e) {
                            console.error('[OfflineSync] Error subiendo adjunto boletín:', e);
                        }
                    }
                }
                
                const callable = httpsCallable(functions, 'sendCustomBulletin');
                await callable({
                    ...payload,
                    storagePaths: [...(payload.storagePaths || []), ...storagePaths]
                });
            } else {
                console.warn(`[OfflineSync] Acción desconocida: ${actionName}`);
            }

            if (isEmailAction) {
                emailActionsProcessed++;
                if (emailActionsProcessed % 15 === 0) {
                    console.log(`[OfflineSync] Procesados ${emailActionsProcessed} correos. Esperando 5 segundos para no saturar...`);
                    await new Promise(resolve => setTimeout(resolve, 5000));
                }
            }

            // Si llegamos aquí, la ejecución fue exitosa
            processedCount++;
            removeFromSyncQueue(item.id);
        } catch (error) {
            console.error(`[OfflineSync] Error procesado item ${item.actionName}:`, error);
            // MANTENER en la cola para reintentar. 
            // Solo descartaríamos si estamos seguros de que el error es irrecuperable (ej: datos mal formados permanentemente)
            // Pero ante la duda, mejor conservar el dato.
            failedItems.push(item);
        }
    }

    // El localStorage ya se actualizó via removeFromSyncQueue para los exitosos.
    // Para los fallidos, el loop terminó y quedaron en failedItems.
    // PERO espera, removeFromSyncQueue ya los borró si fueron exitosos.
    // Al final del loop, la cola en localStorage solo debería tener los que NO se procesaron.
    
    return { success: failedItems.length === 0, processed: processedCount, failed: failedItems.length };
};
